import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { FileText, FileDown, Bus, MapPin, Users, UserCog } from 'lucide-react';
import { exportSummaryReportToPDF } from '../../utils/pdfExport';
import { apiClient } from '../../utils/apiHelper';
import Pagination from '@/components/Pagination';

const TransportReport = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [collegeInfo, setCollegeInfo] = useState(null);
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalStudents, setTotalStudents] = useState(0);

  useEffect(() => {
    fetchReport();
    apiClient.get('/api/tenants/my-college/settings').then(r => setCollegeInfo(r.data)).catch(() => {});
  }, [currentPage, itemsPerPage]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('limit', itemsPerPage);
      
      // Use apiClient which includes auth token
      const routesRes = await apiClient.get('/api/transport/routes').catch(() => ({ data: [] }));
      const vehiclesRes = await apiClient.get('/api/transport/vehicles').catch(() => ({ data: [] }));
      const studentsRes = await apiClient.get(`/api/transport/students?${params.toString()}`).catch(() => ({ data: [] }));
      const driversRes = await apiClient.get('/api/transport/drivers').catch(() => ({ data: [] }));
      
      const routes = Array.isArray(routesRes.data) ? routesRes.data : [];
      const vehicles = Array.isArray(vehiclesRes.data) ? vehiclesRes.data : [];
      const drivers = Array.isArray(driversRes.data) ? driversRes.data : [];
      
      // Handle paginated students response
      let students = [];
      let totalStudentsCount = 0;
      if (Array.isArray(studentsRes.data)) {
        students = studentsRes.data;
        totalStudentsCount = students.length;
      } else {
        students = studentsRes.data?.data || [];
        totalStudentsCount = studentsRes.data?.total || students.length;
      }
      
      setTotalStudents(totalStudentsCount);
      
      setReport({
        totalRoutes: routes.length,
        totalVehicles: vehicles.length,
        activeVehicles: vehicles.filter(v => v.status === 'Active').length,
        totalStudents: totalStudentsCount,
        activeStudents: students.filter(s => s.status === 'Active').length,
        totalDrivers: drivers.length,
        routes: routes,
        vehicles: vehicles,
        students: students,
        drivers: drivers
      });
    } catch (error) {
      console.error('Error:', error);
      setReport({
        totalRoutes: 0, totalVehicles: 0, activeVehicles: 0,
        totalStudents: 0, activeStudents: 0, totalDrivers: 0,
        routes: [], vehicles: [], students: [], drivers: []
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const [exporting, setExporting] = useState(false);

  const exportToPDF = async () => {
    if (!report) return;
    if (exporting) return;
    setExporting(true);
    
    try {
      // Fetch all students for export (without pagination)
      const studentsRes = await apiClient.get('/api/transport/students?limit=1000').catch(() => ({ data: [] }));
      
      let allStudents = [];
      if (Array.isArray(studentsRes.data)) {
        allStudents = studentsRes.data;
      } else {
        allStudents = studentsRes.data?.data || [];
      }
      
      const stats = {
        'Total Routes': report.totalRoutes,
        'Total Vehicles': report.totalVehicles,
        'Active Vehicles': report.activeVehicles,
        'Total Students': allStudents.length,
        'Active Students': allStudents.filter(s => s.status === 'Active').length
      };

      const columns = [
        { header: 'Route Code', dataKey: 'code' },
        { header: 'Route Name', dataKey: 'name' },
        { header: 'Distance', dataKey: 'distance' },
        { header: 'Monthly Fee', dataKey: 'fee' }
      ];

      const data = (report.routes || []).map(r => ({
        code: r.route_code || 'N/A',
        name: r.route_name || 'N/A',
        distance: `${r.distance_km || 0} km`,
        fee: `Rs. ${r.monthly_fee || 0}`
      }));

      exportSummaryReportToPDF('Transport Report', stats, columns, data, 'transport_report.pdf', collegeInfo);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Bus className="w-5 h-5 text-blue-600" />
            Transport Report
          </CardTitle>
          <div className="flex gap-2">
            <Button onClick={fetchReport} disabled={loading}>
              <FileText className="w-4 h-4 mr-2" />{loading ? 'Loading...' : 'Refresh'}
            </Button>
            <Button onClick={exportToPDF} className="bg-green-600 hover:bg-green-700" disabled={!report || loading || exporting}>
              <FileDown className="w-4 h-4 mr-2" />Export PDF
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {report && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600">Total Routes</p>
                      <p className="text-2xl font-bold text-blue-700">{report.totalRoutes}</p>
                    </div>
                    <MapPin className="w-8 h-8 text-blue-400" />
                  </div>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-purple-600">Total Vehicles</p>
                      <p className="text-2xl font-bold text-purple-700">{report.totalVehicles}</p>
                    </div>
                    <Bus className="w-8 h-8 text-purple-400" />
                  </div>
                </div>
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-600">Active Vehicles</p>
                      <p className="text-2xl font-bold text-green-700">{report.activeVehicles}</p>
                    </div>
                    <Bus className="w-8 h-8 text-green-400" />
                  </div>
                </div>
                <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-orange-600">Students</p>
                      <p className="text-2xl font-bold text-orange-700">{report.activeStudents}/{report.totalStudents}</p>
                    </div>
                    <Users className="w-8 h-8 text-orange-400" />
                  </div>
                </div>
                <div className="p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-cyan-600">Drivers</p>
                      <p className="text-2xl font-bold text-cyan-700">{report.totalDrivers}</p>
                    </div>
                    <UserCog className="w-8 h-8 text-cyan-400" />
                  </div>
                </div>
              </div>

              {/* Routes Table */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3">Routes</h3>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-3">Route Code</th>
                        <th className="text-left p-3">Route Name</th>
                        <th className="text-right p-3">Distance (km)</th>
                        <th className="text-right p-3">Monthly Fee</th>
                        <th className="text-center p-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.routes.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-center p-8 text-gray-500">
                            <MapPin className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                            <p>No routes found</p>
                          </td>
                        </tr>
                      ) : (
                        report.routes.map((route, idx) => (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="p-3 font-medium">{route.route_code || 'N/A'}</td>
                            <td className="p-3">{route.route_name || 'N/A'}</td>
                            <td className="p-3 text-right">{route.distance_km || 0} km</td>
                            <td className="p-3 text-right font-semibold text-green-600">₹{(route.monthly_fee || 0).toLocaleString('en-IN')}</td>
                            <td className="p-3 text-center">
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                route.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {route.status || 'Active'}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Vehicles Table */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3">Vehicles</h3>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-3">Vehicle No</th>
                        <th className="text-left p-3">Type</th>
                        <th className="text-right p-3">Capacity</th>
                        <th className="text-left p-3">Driver</th>
                        <th className="text-center p-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.vehicles.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-center p-8 text-gray-500">
                            <Bus className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                            <p>No vehicles found</p>
                          </td>
                        </tr>
                      ) : (
                        report.vehicles.map((vehicle, idx) => (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="p-3 font-medium">{vehicle.vehicle_number || 'N/A'}</td>
                            <td className="p-3">{vehicle.vehicle_type || 'N/A'}</td>
                            <td className="p-3 text-right">{vehicle.seating_capacity || 0}</td>
                            <td className="p-3">{vehicle.driver_name || 'Not Assigned'}</td>
                            <td className="p-3 text-center">
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                vehicle.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {vehicle.status || 'Active'}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Transport Students */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Transport Students ({report.totalStudents})</h3>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-3">Student Name</th>
                        <th className="text-left p-3">Route</th>
                        <th className="text-left p-3">Pickup Point</th>
                        <th className="text-right p-3">Monthly Fee</th>
                        <th className="text-center p-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.students.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-center p-8 text-gray-500">
                            <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                            <p>No transport students found</p>
                          </td>
                        </tr>
                      ) : (
                        report.students.slice(0, 20).map((student, idx) => (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="p-3 font-medium">{student.student_name || 'N/A'}</td>
                            <td className="p-3">{student.route_name || 'N/A'}</td>
                            <td className="p-3">{student.pickup_point || 'N/A'}</td>
                            <td className="p-3 text-right font-semibold text-green-600">₹{(student.monthly_fee || 0).toLocaleString('en-IN')}</td>
                            <td className="p-3 text-center">
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                student.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {student.status || 'Active'}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                  
                  {/* Pagination */}
                  <Pagination
                    currentPage={currentPage}
                    totalPages={Math.ceil(totalStudents / itemsPerPage)}
                    totalItems={totalStudents}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={handleItemsPerPageChange}
                  />
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TransportReport;
